import {createAsyncThunk, createEntityAdapter, createSlice, createSelector} from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/api"
import {
  getClientProjects,
  createClientProject,
  updateClientProject,
  makeProjectActive,
  makeProjectInactive,
  makeProjectArchived,
  updateCLIENTCOVERAGE,
  createCLIENTCOVERAGE,
  certificateByProject,
  getProject,
  updateCLIENTTemplates,
  certificateByClient,
  certificateByClientQueries,
  getClientFromOrganization,
} from "graphql/client"
import {
  createPROJECTVENDORS,
  deletePROJECTVENDORS,
  listCLIENTVENDORS,
} from "graphql/vendor"
import {usToAWSDate} from "utils/dateUtils";
import {organizationActions} from "./organizationSlice"
import { client_mode } from "constants/client_properties";
import cognito_groups from "constants/cognito_groups";
import _ from "underscore";
import { Certificatestatus } from "models";

const adapter = createEntityAdapter()
const initialState = adapter.getInitialState({
  status: "idle",
  categories: {},
  projects: {},
  certificates: null,
  paginatedCertificates: null,
  certificatesCount: 0,
  certificateStatusCounts: {},
  certificateStatusCountsLoading: false,
})

const API = generateClient();

const updateProjectSuccess = (state, action) => {
  state.status = 'success'
  state.projects[action.payload.clientID] = state.projects[action.payload.clientID]?.map(project => {
    if(action.payload?.project?.id === project.id) {
      return {
        ...project,
        ...action.payload.project,
      }
    }
    return project
  })
}

const actions = {
  fetchProjects: createAsyncThunk('client/fetchProjects', async (clientID) => {
    const { data } = await API.graphql({
      query: getClientProjects,
      variables: {
        id: clientID,
      }
    })
    return {
      projects: data?.getCLIENT?.clientProjects?.items,
      clientID: clientID
    }
  }),
  createProject: createAsyncThunk('client/createProject', async ({projectData, clientID}) => {
    const startDate = usToAWSDate(projectData?.startDate)
    const endDate = usToAWSDate(projectData?.endDate)

    const {data} = await API.graphql({
      query: createClientProject,
      variables: {
        ...projectData,
        startDate,
        endDate,
        clientID,
      }
    })
    return {
      success: true,
      clientID,
      project: data?.createPROJECT
    }
  }),
  updateProject: createAsyncThunk('client/updateProject', async ({projectData, clientID}) => {
    const {id, name, description, zip, address, state, requiredDescriptionOfOperation} = projectData
    const input = {id, name, description, address, zip, state, requiredDescriptionOfOperation};
    projectData?.startDate && (input.startDate = usToAWSDate(projectData.startDate))
    projectData?.endDate && (input.endDate = usToAWSDate(projectData?.endDate))
    if(projectData?.requiredCoverages) {
      let requiredCoverages = projectData.requiredCoverages
      if(typeof requiredCoverages !== 'string') {
        delete requiredCoverages.index
        requiredCoverages = JSON.stringify(projectData.requiredCoverages)
      }
      input.requiredCoverages = requiredCoverages;
    }
    const {data} = await API.graphql({
      query: updateClientProject,
      variables: {
        input
      }
    })
    return {
      success: true,
      clientID,
      project: data?.updatePROJECT
    }
  }),
  updateProjectStatus: ({id, status, clientID}) => async (dispatch) => {
    const queryMap = {
      ACTIVE: makeProjectActive,
      INACTIVE: makeProjectInactive,
      ARCHIVED: makeProjectArchived
    }
    const query = queryMap[status]
    if(id && query) {
      const {data} = await API.graphql({
        query,
        variables: {
          id
        }
      })
      dispatch(clientSlice.actions.updateProjectSuccess({ project: data?.updatePROJECT, clientID  }))
      return {
        payload: {
          success: true
        }
      }
    }
  },
  updateClientEmailTemplates: ({clientID, organizationID, templates}) => async (dispatch) => {
    const {data} = await API.graphql({
      query: updateCLIENTTemplates,
      variables: {
        input: {
          id: clientID,
          templates,
        },
      }
    })
    await dispatch(organizationActions.updateSelectedClient({
      id: organizationID,
      updates: {
        templates: data?.updateCLIENT?.templates
      }
    }))
  },
  saveClientCoverage: ({coverages=[], clientID, organizationID}) => async (dispatch) => {
    let updated = []
    await Promise.all(coverages.map(async (coverage) => {
      const input = { ...coverage }
      if(typeof coverage.limits === 'object') {
        input.limits = JSON.stringify(coverage.limits)
      }
      delete input.createdAt;
      delete input.updatedAt;
      delete input.index;
      if(!coverage.id) {
        input.clientID = clientID
      }

      const {data} = await API.graphql({
        query: coverage.id ? updateCLIENTCOVERAGE : createCLIENTCOVERAGE,
        variables: {
          input,
        }
      })
      updated.push(coverage.id ? data.updateCLIENTCOVERAGE : data.createCLIENTCOVERAGE)
      return Promise.resolve()
    }))
    updated = _.sortBy(updated, "createdAt")
    await dispatch(organizationActions.updateSelectedClient({
      id: organizationID,
      updates: {
        clientCoverages: {
          items: updated
        }
      }
    }))
    return {
      payload: {
        coverages: updated,
        success: true
      }
    }
  },
  getCertificateCountsByStatus: createAsyncThunk('client/getCertificateCountsByStatus', async (clientID, {getState}) => {
    const state = getState();
    const { loggedInUser } = state.user;
    const hasSubcontractorFiltering = [cognito_groups.ClientPMRole, cognito_groups.Subcontractor].includes(loggedInUser?.role);
    const userAssignedVendors = loggedInUser?.assignedVendors || [];
    
    // Use existing certificates from state if available
    const existingCertificates = state.client.certificates;
    
    if (existingCertificates && existingCertificates.length > 0) {
      // Calculate counts from existing certificates
      const statusCounts = {
        ALL: existingCertificates.length,
        APPROVED: 0,
        INACTIVE: 0,
        NOT_APPROVED: 0,
        REVIEW: 0,
        EXPIRED: 0,
        REQUESTED: 0
      };
      
      existingCertificates.forEach(cert => {
        if (cert.status && statusCounts[cert.status] !== undefined) {
          statusCounts[cert.status]++;
        }
      });
      
      return {
        success: true,
        statusCounts
      };
    }

    const baseFilter = {};
    // Add filter only if user is ClientPM and has assigned vendors
    if (hasSubcontractorFiltering && userAssignedVendors.length > 0) {
      baseFilter.or = userAssignedVendors.map(vendorId => ({
        clientVendorID: { eq: vendorId }
      }));
    }

    const statuses = ['APPROVED', 'INACTIVE', 'NOT_APPROVED', 'REVIEW', 'EXPIRED', 'REQUESTED'];
    const statusCounts = {};
    let totalCount = 0;

    // Get counts for each status
    for (const status of statuses) {
      let count = 0;
      let nextToken = null;
      
      do {
        const { data } = await API.graphql({
          query: certificateByClient,
          variables: {
            clientID,
            nextToken,
            filter: {
              ...baseFilter,
              status: { eq: status }
            }
          }
        });
        
        if (data?.certificateByClient?.items?.length) {
          count += data.certificateByClient.items.length;
        }
        nextToken = data?.certificateByClient?.nextToken || null;
      } while (nextToken);
      
      statusCounts[status] = count;
      totalCount += count;
    }

    // Get count for ALL (total)
    statusCounts.ALL = totalCount;

    return {
      success: true,
      statusCounts
    };
  }),
  getAllClientCertificates: createAsyncThunk('client/getAllClientCertificates', async (clientID, {getState}) => {
    const state = getState();
    const { loggedInUser } = state.user;
    const hasSubcontractorFiltering = [cognito_groups.ClientPMRole, cognito_groups.Subcontractor].includes(loggedInUser?.role);
    const userAssignedVendors = loggedInUser?.assignedVendors || [];

    const filters = {
    };

    // Add filter only if user is ClientPM and has assigned vendors
  if (hasSubcontractorFiltering && userAssignedVendors.length > 0) {
    filters.filter = {
      or: userAssignedVendors.map(vendorId => ({
        clientVendorID: { eq: vendorId }
      }))
    };
  }
    let nextToken = null;
    let runningCount = 0;
    let initialRun = true;
    let certificates = [];
    do {
      const { data } = await API.graphql({
        query: certificateByClient,
        variables: {
          clientID,
          nextToken,
          filter: {
            // status: {
            //   ne: "REQUESTED"
            // }
            ...filters.filter,
          }
        }
      })
      initialRun = false;
      if(data?.certificateByClient?.items?.length) {
        runningCount += (data.certificateByClient.items.length)
        certificates = certificates.concat(data.certificateByClient.items)
      }
      nextToken = (data?.certificateByClient?.nextToken || null)
    } while (initialRun || nextToken)

    return {
      success: true,
      certificatesCount: runningCount,
      certificates
    }
  }),
  getPaginatedClientCertificates: createAsyncThunk('client/getPaginatedClientCertificates', async({
    clientID,
    sortBy = "lastModifiedDate",
    sortAsc = false,
    nextToken,
    rowsPerPage,
    status,
    statusFilter,
  }, { getState }) => {
    const state = getState();
    const { loggedInUser } = state.user;
    const hasSubcontractorFiltering = [cognito_groups.ClientPMRole, cognito_groups.Subcontractor].includes(loggedInUser?.role);
    const userAssignedVendors = loggedInUser?.assignedVendors || [];

    const filters = {
    };

    // Add filter only if user is ClientPM and has assigned vendors
  if (hasSubcontractorFiltering && userAssignedVendors.length > 0) {
    filters.filter = {
      or: userAssignedVendors.map(vendorId => ({
        clientVendorID: { eq: vendorId }
      }))
    };
  }
    const sortQueriesMapper = {
      activeBylastModifiedDate: "getActiveCertificates",
    }

    const indexNameMapper = {
      default: "byCLIENT",
      certificateDate: "byCLIENTsortCERTIFICATEDATE",
      insured: "byCLIENTsortINSURED",
      producer: "byCLIENTsortPRODUCER",
      policyExp: "byCLIENTsortFirstExp",
      lastModifiedDate: "byCLIENTsortUPDATEDAT",
      sentDate: "byCLIENTsortUPDATEDAT",
      createdDate: "byCLIENTsortCREAEDAT"
    }

    // const queryName = sortQueriesMapper[sortBy] || sortQueriesMapper.default
    const queryName = sortQueriesMapper.activeBylastModifiedDate
    let rowsCount = rowsPerPage;
    let result = [];
    let resultNextToken = null;

    do {
    const { data } = await API.graphql({
      query: certificateByClientQueries[queryName],
      variables: {
        clientID,
        sortDirection: sortAsc ? "ASC" : "DESC",
        limit: rowsCount - result.length,
        nextToken: resultNextToken || nextToken,
        indexName: indexNameMapper[sortBy] || indexNameMapper.default,
        status: Certificatestatus.MULTI_AGENT,
        statusFilter: "<>",
        ...(status && { status: status}),
        ...(statusFilter && { statusFilter: statusFilter }),
        ...filters,
      }
    })

      result.push(...data?.[queryName]?.items);
      resultNextToken = data?.[queryName]?.nextToken;
    } while(result.length < rowsCount && resultNextToken)

    return {
      certificates: result,
      nextToken: resultNextToken,
      success: true,
    }
  }),
  getProjectCertificates: createAsyncThunk('client/getProjectCertificates', async (projectID, {getState}) => {
    const state = getState();
    const { loggedInUser } = state.user;
    const hasSubcontractorFiltering = [cognito_groups.ClientPMRole, cognito_groups.Subcontractor].includes(loggedInUser?.role);
    const userAssignedVendors = loggedInUser?.assignedVendors || [];

    const filters = {
    };

    // Add filter only if user is ClientPM and has assigned vendors
    if (hasSubcontractorFiltering && userAssignedVendors.length > 0) {
    filters.filter = {
      or: userAssignedVendors.map(vendorId => ({
        clientVendorID: { eq: vendorId }
      }))
    };
  }
    const {data} = await API.graphql({
      query: certificateByProject,
      variables: {
        projectID,
          ...filters,
      }
    })

    return {
      certificates: data.certificateByProject.items,
      success: true
    }
  }),
  updateProjectVendors: createAsyncThunk('client/updateProjectVendors', async ({projectID, selectedVendors = {}, existingProjectVendors}) => {
    let projectVendorsToAdd = {...selectedVendors}
    const projectVendorsToRemove = []
    existingProjectVendors?.forEach(existingProjectVendor => {
      if(projectVendorsToAdd[existingProjectVendor.clientVendor?.id]) {
        delete projectVendorsToAdd[existingProjectVendor.clientVendor.id]
      } else {
        projectVendorsToRemove.push(existingProjectVendor.id)
      }
    })
    projectVendorsToAdd = Object.keys(projectVendorsToAdd).map(clientVendorID => ({
      clientVendorID,
      projectID
    }))

    if(projectVendorsToAdd.length) {
      await Promise.all(projectVendorsToAdd.map(async (projectVendor) => {
        await API.graphql({
          query: createPROJECTVENDORS,
          variables: {
            input: {
              ...projectVendor
            }
          }
        })
        return Promise.resolve()
      }))
    }
    if(projectVendorsToRemove.length) {
      await Promise.all(projectVendorsToRemove.map(async (projectVendorID) => {
        await API.graphql({
          query: deletePROJECTVENDORS,
          variables: {
            input: {
              id: projectVendorID
            }
          }
        })
        return Promise.resolve()
      }))
    }
    const result = await API.graphql({
      query: getProject,
      variables: {
        id: projectID
      }
    })
    return {
      success: true,
      clientID: result?.data?.getPROJECT?.clientID,
      project: result?.data?.getPROJECT,
    }
  }),
  listClientVendorsForClient: createAsyncThunk('client/listClientVendorsForClient', async ({ clientID, filter = {} }, {getState}) => {
    const state = getState();
    const { loggedInUser } = state.user;
    const hasSubcontractorFiltering = [cognito_groups.ClientPMRole, cognito_groups.Subcontractor].includes(loggedInUser?.role);
    const userAssignedVendors = loggedInUser?.assignedVendors || [];

    const filters = {
    };

    // Add filter only if user is ClientPM and has assigned vendors
    if (hasSubcontractorFiltering && userAssignedVendors.length > 0) {
    filters.filter = {
      or: userAssignedVendors.map(vendorId => ({
        id: { eq: vendorId }
      }))
    };
  }
    let nextToken = true
    let clientVendors = []
    while (nextToken) {
      const { data } = await API.graphql({
        query: listCLIENTVENDORS,
        variables: {
          filter: {
            ...filter,
            clientID: {
              eq: clientID
            },
            ...filters.filter,
          },
          nextToken: nextToken === true ? undefined : nextToken
        }
      })
      nextToken = data?.listCLIENTVENDORS?.nextToken
      clientVendors = [
        ...clientVendors,
        ...data?.listCLIENTVENDORS?.items
      ]
    }

    return {
      success: true,
      clientVendors: clientVendors.sort((a, b) => a.vendor.name.localeCompare(b.vendor.name))
    }
  }),
  getClientFromOrganization: createAsyncThunk('client/getClientFromOrganization', async ({ organizationID }) => {

    const { data } = await API.graphql({
      query: getClientFromOrganization,
      variables: {
        organizationID,
      }
    })
    return {
      success: true,
      client: data?.listCLIENTS?.items?.[0]
    }
  }),
  listClientCertificates: createAsyncThunk('client/listClientCertificates', async ({
    filter,
    clientID
  }, {getState}) => {
    const state = getState();
    const { loggedInUser } = state.user;
    const hasSubcontractorFiltering = [cognito_groups.ClientPMRole, cognito_groups.Subcontractor].includes(loggedInUser?.role);
    const userAssignedVendors = loggedInUser?.assignedVendors || [];

    const filters = {
    };

    // Add filter only if user is ClientPM and has assigned vendors
  if (hasSubcontractorFiltering && userAssignedVendors.length > 0) {
    filters.filter = {
      ...filter,
      or: userAssignedVendors.map(vendorId => ({
        clientVendorID: { eq: vendorId }
      }))
    };
  }
    let nextToken = true
    let certList = []
    while (nextToken) {
      const { data } = await API.graphql({
        query: certificateByClient,
        variables: {
          clientID,
          filter,
          nextToken: nextToken === true ? undefined : nextToken,
          ...filters
        }
      })
      nextToken = data?.certificateByClient?.nextToken
      certList = [
        ...certList,
        ...data?.certificateByClient?.items
      ]
    }

    return {
      success: true,
      certificates: certList.sort((cert1, cert2) => (
        cert1.firstExp > cert2.firstExp ? 1 : cert1.firstExp < cert2.firstExp ? -1 : 0
      ))
    }
  }),
}

export const clientSlice = createSlice({
  name: "client",
  initialState,
  reducers: {
    resetState: (state, action) => {
      state.categories = {};
      state.projects = {};
      state.certificates = null;
      state.paginatedCertificates = null;
      state.certificatesCount = 0;
    },
    updateProjectSuccess
  },
  extraReducers: builder => {
    builder
      // fetching projects
      .addCase(actions.fetchProjects.pending, state => {
        state.status = 'loading'
      })
      .addCase(actions.fetchProjects.fulfilled, (state, action) => {
        state.projects[action.payload.clientID] = action.payload.projects || []
        state.status = 'success'
      })
      .addCase(actions.fetchProjects.rejected, (state, action) => {
        state.status = 'failure'
      })
      // create project
      .addCase(actions.createProject.pending, state => {
        state.status = 'loading'
      })
      .addCase(actions.createProject.fulfilled, (state, action) => {
        state.status = 'success'
        if(action.payload?.project) {
          if(state.projects[action.payload.clientID]?.length) {
            state.projects[action.payload.clientID].push(action.payload.project)
          } else {
            state.projects[action.payload.clientID] = [action.payload.project]
          }
        }
      })
      .addCase(actions.createProject.rejected, (state, action) => {
        state.status = 'failure'
      })
      // update project
      .addCase(actions.updateProject.pending, state => {
        state.status = 'loading'
      })
      .addCase(actions.updateProject.fulfilled, updateProjectSuccess)
      .addCase(actions.updateProject.rejected, (state, action) => {
        state.status = 'failure'
      })
      // get client certificates
      .addCase(actions.getPaginatedClientCertificates.pending, state => {
        state.status = 'loading'
      })
      .addCase(actions.getPaginatedClientCertificates.fulfilled, (state, action) => {
        state.paginatedCertificates = action.payload?.certificates
        state.status = 'success'
      })
      .addCase(actions.getPaginatedClientCertificates.rejected, (state, action) => {
        state.status = 'failure'
      })
      // get client certificates count
      .addCase(actions.getAllClientCertificates.fulfilled, (state, action) => {
        state.certificatesCount = action.payload?.certificatesCount;
        state.certificates = action.payload?.certificates;
      })
      // get certificate counts by status
      .addCase(actions.getCertificateCountsByStatus.pending, (state, action) => {
        state.certificateStatusCountsLoading = true;
      })
      .addCase(actions.getCertificateCountsByStatus.fulfilled, (state, action) => {
        state.certificateStatusCounts = action.payload?.statusCounts || {};
        state.certificateStatusCountsLoading = false;
      })
      .addCase(actions.getCertificateCountsByStatus.rejected, (state, action) => {
        state.certificateStatusCountsLoading = false;
      })
      // update project vendors
      .addCase(actions.updateProjectVendors.pending, state => {
        state.status = 'loading'
      })
      .addCase(actions.updateProjectVendors.fulfilled, updateProjectSuccess)
      .addCase(actions.updateProjectVendors.rejected, (state, action) => {
        state.status = 'failure'
      })
  }
})

export const clientState = state => state.client
export const projectState = state => state.client.projects
export const clientClassificationsState = state => {
  if(state.organization?.selectedClient) {
    const clientMode = state.organization?.selectedClient?.mode;
    const classificationKey = clientMode === client_mode.category ? "categories" : "projects"
    return state.client?.[classificationKey]?.[state.organization.selectedClient.clientID]
  }
}
export const certificatesState = createSelector(
  [
    state => state.client.certificates,
    state => state.client.paginatedCertificates,
    state => state.client.certificatesCount,
    state => state.client.certificateStatusCounts,
    state => state.client.certificateStatusCountsLoading
  ],
  (certificates, paginatedCertificates, certificatesCount, certificateStatusCounts, certificateStatusCountsLoading) => ({
    certificates,
    paginatedCertificates,
    certificatesCount,
    certificateStatusCounts,
    certificateStatusCountsLoading
  })
)
export const clientActions = {
  ...clientSlice.actions,
  ...actions,
}

export default clientSlice.reducer
